import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAcceptInviteMutation } from "../api/invite/useAcceptInviteMutation";
import { ErrorMessages } from "../constants/Errors";
import Loader from "@amzn/meridian/loader";
import { centerContentStyle } from "../styles/Users.styles";
import { CurrentStageAndConfig, getCurrentStageAndConfig } from "@amzn/rangoli-seller-desktop-async-call-wrapper/src/index";
import {
    toasterData,
    handleOpenToaster,
} from "@amzn/rangoli-seller-desktop-utils/src/index";
import { usePostMerchantRegisterMutation } from "@amzn/rangoli-seller-desktop-api-utils/src/index";
import { RedirectType } from "../constants/RedirectTypes";
import { STRINGS } from "../constants/strings";
import Row from '@amzn/meridian/row';
import Heading from "@amzn/meridian/heading";
import { useGetInviteQuery } from "../api/invite/useGetInviteQuery";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import { container, logo, card, storeIcon, pageColor } from "../styles/Users.styles";
import Footer from '../components/Footer/Footer';

const AcceptInvitePage = () => {

    const [searchParams] = useSearchParams();
    const inviteeId = searchParams.get("inviteeId");
    const { data: inviteDetail, isLoading, isError, error: queryError } = useGetInviteQuery(inviteeId);
    const merchantRegisterMutation = usePostMerchantRegisterMutation();
    const acceptInviteMutation = useAcceptInviteMutation();
    const { config: currentStage }: CurrentStageAndConfig = getCurrentStageAndConfig();
    const [error, setError] = useState<String>(null);
    const [isRedirectionToLogin, setIsRedirectionToLogin] = useState<boolean>(false);
    const handleErrorDescription = (errorDescription: string): string => {
        return errorDescription === STRINGS.OLD_INCORRECT_MOBILE_ERROR_MESSAGE ? STRINGS.NEW_INCORRECT_MOBILE_ERROR_MESSAGE : errorDescription;
    }

    useEffect(() => {
        const registerMerchant = async (): Promise<void> => {
            try {
                await merchantRegisterMutation.mutateAsync();
            } catch (error: any) {
                setIsRedirectionToLogin(false);
                const status = error?.response?.status;
                const errorMessage = status === 400
                    ? handleErrorDescription(error?.response?.data?.description)
                    : ErrorMessages.apiFailedErrorMessage;
                handleOpenToaster({
                    ...toasterData,
                    text: errorMessage
                }, 5000);
                setError(errorMessage);
            }
        };
        void registerMerchant();
    }, []);

    const handleJoinStoreClick = () => {
        acceptInviteMutation.mutate(inviteeId, {
            onSuccess: (response: any) => {
                const data = response?.data;
                if (data?.redirectUrl) {
                    setIsRedirectionToLogin(true);
                    switch (data.redirectType) {
                        case RedirectType.LOGIN:
                            handleOpenToaster({
                                type: "informational",
                                ...toasterData,
                                text: ErrorMessages.userNotLoggedInError
                            }, 5000);
                            break;

                        case RedirectType.LOGIN_AND_SECURITY_EMAIL:
                            handleOpenToaster({
                                type: "informational",
                                ...toasterData,
                                text: ErrorMessages.inValidEmailError
                            }, 5000);
                            break;

                        case RedirectType.LOGIN_AND_SECURITY_MOBILE:
                            handleOpenToaster({
                                type: "informational",
                                ...toasterData,
                                text: ErrorMessages.inValidMobileNumberError
                            }, 5000);
                            break;
                    }
                    setTimeout(() => {
                        return window.location.replace(data.redirectUrl);
                    }, 5000)
                } else {
                    setIsRedirectionToLogin(false);
                    localStorage.setItem('shopId', inviteDetail.storeId);
                    handleOpenToaster({
                        ...toasterData,
                        type: "success",
                        text: STRINGS.ACCEPT_INVITE_SUCCESS
                    }, 5000);
                    setTimeout(() => {
                        window.location.replace(currentStage.returnToUrl);
                    }, 5000);
                }
            },
            onError: (error: any) => {
                setIsRedirectionToLogin(false);
                const status = error?.response?.status;
                const errorMessage = status === 400
                    ? handleErrorDescription(error?.response?.data?.description)
                    : ErrorMessages.apiFailedErrorMessage
                handleOpenToaster({
                    ...toasterData,
                    text: errorMessage
                }, 5000);
                setError(errorMessage)
            }
        });
        if (acceptInviteMutation.isLoading) {
            return (
                <div className={centerContentStyle}>
                    <h1>{STRINGS.ACCEPT_INVITE_PROCESSING}</h1>
                    <Loader size="medium" />
                </div>
            );
        }
        else if (acceptInviteMutation.isSuccess) {
            return (
                <div className={centerContentStyle}>
                    <h1>
                        {isRedirectionToLogin
                            ? STRINGS.REDIRECTING
                            : STRINGS.ACCEPT_INVITE_SUCCESS
                        }
                    </h1>
                    <Loader size="medium" />
                </div>
            );
        }
        else {
            return (
                <div className={centerContentStyle}>
                    <h1>{STRINGS.ACCEPT_INVITE_FAILURE}</h1>
                    <b>{error}</b>
                    {
                        error === STRINGS.NEW_INCORRECT_MOBILE_ERROR_MESSAGE && (
                            <p>{STRINGS.CONTACT_STORE_OWNER_MESSAGE}</p>
                        )
                    }
                </div>
            );
        }
    }

    if (isError && queryError) {
        return (
            <div className={centerContentStyle}>
                <h1>{STRINGS.ACCEPT_INVITE_FAILURE}</h1>
                <b>{queryError.description || ErrorMessages.somethingWentWrong}</b>
            </div>
        );
    } else if (isLoading) {
        return (
            <div className={centerContentStyle}>
                <h1>{STRINGS.LOADING}</h1>
            </div>
        );
    }

    return (
        <div className={pageColor}>
            <Column className={container}>
                {
                    inviteDetail && (
                        <>
                            <Column>
                                <img src={STRINGS.APP_LOGO} className={logo} />
                                <Text type="d100"> {STRINGS.JOIN} {inviteDetail.storeName}</Text>
                                <Text type="b300">
                                    {inviteDetail.requesterName}&nbsp;
                                    (<a href={`mailto:${inviteDetail.requesterEmail}`}>{inviteDetail.requesterEmail}</a>)
                                    &nbsp;{STRINGS.INVITED_AS_STAFF}&nbsp;{inviteDetail.storeName}!
                                </Text>
                                <Column height="250px" heights="fill" alignmentHorizontal="center" className={card}
                                    alignmentVertical="top"
                                    spacing="400"
                                >
                                    {inviteDetail?.storeLogoUrl ? (
                                        <img
                                            src={inviteDetail?.storeLogoUrl}
                                            alt="Profile"
                                            className={storeIcon}
                                        />
                                    ) : (
                                        <Row
                                            className={storeIcon}
                                            backgroundColor={inviteDetail?.storeName?.charAt(0)?.toUpperCase() && "#F26222"}
                                            alignmentHorizontal="center"
                                            alignmentVertical="center"
                                        >
                                            {inviteDetail?.storeName?.charAt(0)?.toUpperCase()}
                                        </Row>
                                    )}
                                    <Heading level={3}>{inviteDetail.storeName}</Heading>
                                    <Text type="b400">
                                        <a href={inviteDetail.storeLink} target='_blank'>{inviteDetail.storeLink}</a>
                                    </Text>
                                    <Button disabled={isLoading} onClick={handleJoinStoreClick} size="large">{STRINGS.JOIN_STORE}</Button>
                                </Column>
                                <Footer />
                            </Column>
                        </>
                    )}
            </Column>
        </div>
    )
};

export default AcceptInvitePage;